// Les INSIGHTS d'une étude — et leur dérivation.
//
// Une étude recueille des verbatims ; ce bloc en tire des conclusions. Le
// bouton « Dériver des insights » demande au modèle d'INTERPRÉTER le terrain :
// pour chaque proposition, le constat, le pourquoi, l'opportunité, une
// recommandation, et les verbatims qui l'établissent.
//
// Ce sont des PROPOSITIONS. Rien n'est enregistré tant qu'on ne l'a pas décidé :
// chaque carte s'enregistre, se reformule (crayon) ou s'écarte (poubelle). Comme
// pour les études recommandées, la carte est repliée et s'ouvre au clic.
import { Part } from './Part';
import { useId, useState, type FormEvent } from 'react';
import { useCreateInsight, useDeriveInsights, useInsights, useStudyVerbatims } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { InsightProposal, ProblemType, RecoKind } from '../api/types';
import { Button, Chip, ErrorBanner, IconButton, Select } from '../components/ui';
import { Link } from '../router';
import { PROBLEM_TYPES, PROBLEM_TYPE_LABEL, RECO_KINDS, RECO_KIND_LABEL } from './labels';
import '../plan/plan.css';
import { SeverityChip } from '../taxonomy/Chips';
import { PROBLEM_HUE, RECO_HUE, SEVERITY_HUE } from '../taxonomy/colors';
import './insights.css';

/** En dessous, il n'y a rien à interpréter — le serveur refuse aussi. */
const MIN_VERBATIMS = 2;
const SEVERITIES = ['Bloquant', 'Levier'];

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

export function StudyInsightsSection({ projectId, studyId, canWrite }: { projectId: string; studyId: string; canWrite: boolean }) {
  const insights = useInsights(projectId);
  const verbatims = useStudyVerbatims(studyId);
  const derive = useDeriveInsights(studyId);
  // Les propositions en attente de décision. `null` = aucune dérivation lancée.
  const [pending, setPending] = useState<InsightProposal[] | null>(null);

  const fromStudy = (insights.data ?? []).filter((i) => i.studies.some((s) => s.id === studyId));
  const verbatimCount = verbatims.data?.length ?? 0;
  const enough = verbatimCount >= MIN_VERBATIMS;
  const insightsTo = `/projet/${encodeURIComponent(projectId)}/insights`;

  async function run() {
    try {
      const res = await derive.mutateAsync();
      setPending(res.proposals);
    } catch {
      /* rendu par le bandeau */
    }
  }

  const drop = (key: string) => setPending((list) => (list ?? []).filter((p) => p.key !== key));
  const replace = (next: InsightProposal) => setPending((list) => (list ?? []).map((p) => (p.key === next.key ? next : p)));

  return (
    <section className="test-block" aria-labelledby="study-insights-title">
      <div className="block-head">
        <h2 className="t-h3" id="study-insights-title">
          Insights de l’étude {fromStudy.length > 0 ? <span className="plan-count">{fromStudy.length}</span> : null}
        </h2>
        {canWrite && enough ? (
          <Button variant="accent" loading={derive.isPending} onClick={() => void run()}>
            {pending === null ? 'Dériver des insights' : 'Relancer la dérivation'}
          </Button>
        ) : null}
      </div>
      <p className="zy-intro">
        Ce que le terrain apprend : le constat, pourquoi les utilisateurs se comportent ainsi, et ce
        qu’il faudrait changer. Chaque insight cite les verbatims qui l’établissent.
      </p>

      {!enough && !verbatims.isPending ? (
        <p className="zy-texte-muet">
          Il faut au moins {MIN_VERBATIMS} verbatims dans « Terrain recueilli » pour dériver des insights.
        </p>
      ) : null}
      {derive.error ? <ErrorBanner>{errorMessage(derive.error)}</ErrorBanner> : null}

      {pending !== null ? (
        <div className="derive-block">
          <p className="derive-head">
            <span className="t-mono">Propositions de l’IA</span>
            {derive.data?.mode === 'fake' ? <span className="derive-mode">démonstration locale, sans modèle</span> : null}
          </p>
          {pending.length === 0 ? (
            <p className="zy-texte-muet">
              Plus rien à décider : les propositions ont été enregistrées ou écartées, ou le terrain ne
              contient rien de nouveau par rapport aux insights déjà établis.
            </p>
          ) : (
            <>
              <p className="zy-texte-muet">
                {derive.data ? `${derive.data.verbatims} verbatims lus${derive.data.truncated ? ' (les plus récents)' : ''}. ` : ''}
                Rien n’est enregistré tant que vous ne l’avez pas décidé. Relisez les preuves avant d’accepter.
              </p>
              <ol className="derive-list">
                {pending.map((p) => (
                  <ProposalCard key={p.key} proposal={p} projectId={projectId} onSaved={() => drop(p.key)} onDismiss={() => drop(p.key)} onChange={replace} />
                ))}
              </ol>
            </>
          )}
        </div>
      ) : null}

      {fromStudy.length > 0 ? (
        <ul className="derive-saved">
          {fromStudy.map((i) => (
            <li key={i.id}>
              <span className="derive-saved-title">{i.title}</span>
              <span className="derive-saved-meta">
                {i.origin === 'ia' ? <Chip ia title="Proposé par la dérivation, puis accepté">IA</Chip> : null}
                <SeverityChip value={i.severity} />
                <Chip>{i.evidence_count} preuve{i.evidence_count > 1 ? 's' : ''}</Chip>
              </span>
            </li>
          ))}
          <li className="derive-saved-more">
            <Link to={insightsTo}>Voir tous les insights du projet</Link>
          </li>
        </ul>
      ) : pending === null && enough ? (
        <p className="zy-texte-muet">Aucun insight ne s’appuie encore sur cette étude.</p>
      ) : null}
    </section>
  );
}

/** Un clic sur la carte l'ouvre — sauf s'il vise un bouton, un lien ou un champ. */
function toggleFromCard(e: React.MouseEvent, toggle: () => void) {
  if ((e.target as HTMLElement).closest('button, a, input, textarea, select, label, .plan-rec-detail, .plan-rec-actions')) return;
  toggle();
}

function ProposalCard({
  proposal: p, projectId, onSaved, onDismiss, onChange,
}: {
  proposal: InsightProposal; projectId: string; onSaved: () => void; onDismiss: () => void; onChange: (next: InsightProposal) => void;
}) {
  const create = useCreateInsight(projectId);
  // Repliée par défaut ; un clic sur la carte l'ouvre.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const detailId = useId();

  async function save() {
    try {
      await create.mutateAsync({
        title: p.title,
        ...(p.summary ? { summary: p.summary } : {}),
        ...(p.why ? { why: p.why } : {}),
        ...(p.opportunity ? { opportunity: p.opportunity } : {}),
        ...(p.recommendation ? { recommendation: p.recommendation } : {}),
        ...(p.recommendation && p.recommendationKind ? { recommendationKind: p.recommendationKind } : {}),
        ...(p.severity ? { severity: p.severity } : {}),
        ...(p.problemType ? { problemType: p.problemType } : {}),
        ...(p.pillarId ? { pillarId: p.pillarId } : {}),
        // La marque reste, même reformulé : c'est une conclusion PROPOSÉE.
        origin: 'ia',
        verbatimIds: p.evidenceIds,
      });
      onSaved();
    } catch {
      /* rendu par le bandeau */
    }
  }

  if (editing) {
    return (
      <li className="plan-rec">
        <ProposalEditForm proposal={p} onCancel={() => setEditing(false)} onDone={(next) => { onChange(next); setEditing(false); setOpen(true); }} />
      </li>
    );
  }

  return (
    <li className="plan-rec plan-rec-click" onClick={(e) => toggleFromCard(e, () => setOpen((v) => !v))}>
      <div className="plan-rec-top">
        <Chip ia>IA</Chip>
        {p.severity ? <Chip hue={SEVERITY_HUE[p.severity] ?? 'cyan'}>{p.severity}</Chip> : null}
        {p.problemType ? <Chip hue={PROBLEM_HUE[p.problemType]}>{PROBLEM_TYPE_LABEL[p.problemType]}</Chip> : null}
        {p.recommendationKind ? <Chip hue={RECO_HUE[p.recommendationKind]}>{RECO_KIND_LABEL[p.recommendationKind]}</Chip> : null}
        <Chip>{p.evidence.length} preuve{p.evidence.length > 1 ? 's' : ''}</Chip>
        <span className="plan-rec-tools">
          <IconButton icon="edit" label={`Modifier « ${p.title} »`} onClick={() => setEditing(true)} />
          <IconButton icon="trash" danger label={`Écarter « ${p.title} »`} onClick={onDismiss} />
        </span>
      </div>
      <button type="button" className="plan-rec-title plan-rec-open" aria-expanded={open} aria-controls={detailId} onClick={() => setOpen((v) => !v)}>
        {p.title}
      </button>

      {open ? (
        <div className="plan-rec-detail" id={detailId}>
          <dl className="insight-parts">
            {p.summary ? <Part label="Constat" text={p.summary} /> : null}
            {p.why ? <Part label="Pourquoi" text={p.why} /> : null}
            {p.opportunity ? <Part label="Opportunité" text={p.opportunity} /> : null}
            {p.recommendation ? <Part label="Recommandation" text={p.recommendation} /> : null}
          </dl>
          <p className="plan-rec-target">
            <span>Preuves</span>
          </p>
          <ul className="derive-evidence">
            {p.evidence.map((e) => (
              <li key={e.id}>
                <span className={`dot dot-${e.sentiment ?? 'neu'}`} aria-hidden="true" />
                <span>
                  « {e.text} »
                  {e.speaker || e.sourceRef ? <span className="evidence-meta">{[e.speaker, e.sourceRef].filter(Boolean).join(' · ')}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          {create.error ? <ErrorBanner>{errorMessage(create.error)}</ErrorBanner> : null}
          <div className="plan-rec-actions">
            <Button variant="accent" loading={create.isPending} onClick={() => void save()}>
              Enregistrer cet insight
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Zone de texte à identifiant propre : plusieurs cartes peuvent être en édition. */
function Area({ label, value, onChange, rows = 2 }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
  const id = useId();
  return (
    <div className="zy-field">
      <label className="zy-field__label" htmlFor={id}>{label}</label>
      <textarea id={id} className="zy-input" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** Reformuler une proposition AVANT de l'enregistrer. Les preuves ne changent
 *  pas : on peut réécrire une conclusion, pas les faits qui la fondent. */
function ProposalEditForm({ proposal: p, onCancel, onDone }: { proposal: InsightProposal; onCancel: () => void; onDone: (next: InsightProposal) => void }) {
  const [title, setTitle] = useState(p.title);
  const [summary, setSummary] = useState(p.summary ?? '');
  const [why, setWhy] = useState(p.why ?? '');
  const [opportunity, setOpportunity] = useState(p.opportunity ?? '');
  const [recommendation, setRecommendation] = useState(p.recommendation ?? '');
  const [kind, setKind] = useState<RecoKind | ''>(p.recommendationKind ?? '');
  const [severity, setSeverity] = useState(p.severity ?? '');
  const [problemType, setProblemType] = useState<ProblemType | ''>(p.problemType ?? '');
  const titleId = useId();
  const problemId = useId();
  const kindId = useId();
  const severityId = useId();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onDone({
      ...p,
      title: title.trim(),
      summary: summary.trim() || null,
      why: why.trim() || null,
      opportunity: opportunity.trim() || null,
      recommendation: recommendation.trim() || null,
      recommendationKind: recommendation.trim() ? (kind || 'autre') : null,
      severity: severity || null,
      problemType: problemType || null,
    });
  }

  return (
    <form className="write-form plan-edit" onSubmit={submit} noValidate>
      <p className="opt-subtitle">Reformuler la proposition</p>
      <div className="zy-field">
        <label className="zy-field__label" htmlFor={titleId}>Titre</label>
        <input id={titleId} className="zy-input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <Area label="Constat" value={summary} onChange={setSummary} />
      <Area label="Pourquoi" value={why} onChange={setWhy} />
      <Area label="Opportunité" value={opportunity} onChange={setOpportunity} />
      <Area label="Recommandation" value={recommendation} onChange={setRecommendation} />
      <div className="plan-edit-row">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={kindId}>Nature de la recommandation</label>
          <Select id={kindId} value={kind} onChange={(e) => setKind(e.target.value as RecoKind | '')}>
            <option value="">—</option>
            {RECO_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={severityId}>Sévérité</label>
          <Select id={severityId} value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">—</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={problemId}>Type de problème</label>
          <Select id={problemId} value={problemType} onChange={(e) => setProblemType(e.target.value as ProblemType | '')}>
            <option value="">—</option>
            {PROBLEM_TYPES.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </div>
      </div>
      <p className="zy-texte-muet">Les preuves citées ne changent pas : elles viennent des verbatims de l’étude.</p>
      <div className="plan-rec-actions plan-edit-actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Annuler</Button>
        <Button type="submit" variant="accent" disabled={!title.trim()}>Valider la reformulation</Button>
      </div>
    </form>
  );
}
